import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Star, StarOff } from "lucide-react";

import EntityDetailShell from "../../components/engage/EntityDetailShell";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ENGAGE_PATIENT_URL } from "../../constants/api";

const PatientDetail = () => {
    const { id } = useParams();
    const { fetch: apiFetch, update } = useApiMethods({ throwOnFail: true });

    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        apiFetch(ENGAGE_PATIENT_URL(id))
            .then(res => setPatient(res?.data || res))
            .catch(() => ToastMsg('error', 'Failed to load patient'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const toggleVip = async () => {
        if (!patient) return;
        try {
            const body = { ...patient, is_vip: !patient.is_vip };
            const res = await update(ENGAGE_PATIENT_URL(id), body);
            setPatient(res?.data || res);
            ToastMsg('success', patient.is_vip ? 'VIP unmarked' : 'Marked VIP');
        } catch { ToastMsg('error', 'Update failed'); }
    };

    const displayName = patient
        ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim()
        : '';

    const badges = patient ? [
        { label: 'Patient', className: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300' },
        ...(patient.is_vip
            ? [{ label: 'VIP', className: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' }]
            : []),
        ...(patient.tags || []).map(t => ({
            label: t,
            className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
        })),
    ] : [];

    const infoRows = patient ? [
        { label: 'ID',                value: `#${patient.id}`, mono: true },
        { label: 'UHID',              value: patient.uhid || null, mono: true },
        { label: 'eMedHub Patient',   value: patient.emedhub_patient_id ? `#${patient.emedhub_patient_id}` : null, mono: true },
        { label: 'Name',              value: displayName },
        { label: 'Gender',            value: patient.gender || null },
        { label: 'Date of birth',     value: patient.date_of_birth ? dayjs(patient.date_of_birth).format('MMM D, YYYY') : null },
        { label: 'Mobile',            value: patient.mobile },
        { label: 'Alt mobile',        value: patient.alt_mobile || null },
        { label: 'Email',             value: patient.email || null },
        { label: 'City',              value: patient.city || null },
        { label: 'State',             value: patient.state || null },
        { label: 'Language',          value: patient.language_display || patient.preferred_language },
        { label: 'Prefers WhatsApp',  value: patient.prefer_whatsapp ? 'Yes' : 'No' },
        { label: 'Prefers SMS',       value: patient.prefer_sms ? 'Yes' : 'No' },
        { label: 'Prefers Email',     value: patient.prefer_email ? 'Yes' : 'No' },
        { label: 'Prefers Call',      value: patient.prefer_call ? 'Yes' : 'No' },
        { label: 'Marketing consent', value: patient.consent_marketing ? 'Yes' : 'No' },
        { label: 'Data-share consent',value: patient.consent_data_share ? 'Yes' : 'No' },
        { label: 'Family head',       value: patient.family_head_name || null },
        { label: 'Source enquiry',    value: patient.source_enquiry ? `Enquiry #${patient.source_enquiry}` : null },
        { label: 'Created',           value: patient.created_date ? dayjs(patient.created_date).format('MMM D, YYYY') : null },
        { label: 'Last updated',      value: patient.modified_date ? dayjs(patient.modified_date).fromNow() : null },
    ] : [];

    const actions = patient ? [
        {
            label: patient.is_vip ? 'Unmark VIP' : 'Mark VIP',
            onClick: toggleVip,
            icon: patient.is_vip ? StarOff : Star,
        },
    ] : [];

    return (
        <EntityDetailShell
            entityType="patient"
            entityId={Number(id)}
            entity={patient}
            loading={loading}
            onRefresh={load}
            title={displayName || 'Patient'}
            subtitle={patient ? `Patient #${patient.id} · ${patient.mobile}` : ''}
            badges={badges}
            infoRows={infoRows}
            actions={actions}
        />
    );
};

export default PatientDetail;
